import { buildSectionSelector, normalizeSectionId, sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, onDocumentReady } from '@theme/utilities';

/**
 * Hydrates a section using the Section Rendering API preserving states.
 * Only updates elements with data-hydration-key attribute. This ensures that non-targeted nodes do not have their markup/state updated unnecessarily.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {URL} [url] - The URL to render the section from
 */
async function hydrateSection(sectionId, url) {
  const normalizedId = normalizeSectionId(sectionId);
  const section = document.getElementById(buildSectionSelector(normalizedId));

  if (!section || section.dataset.hydrated === 'true') {
    return;
  }

  try {
    await sectionRenderer.renderSection(normalizedId, { cache: false, url, mode: 'hydration' });
    section.dataset.hydrated = 'true';
  } catch (error) {
    // Hydration is progressive enhancement — the section is already
    // server-rendered and fully functional. If the Section Rendering API
    // request fails (e.g. a transient 503) or its response doesn't contain the
    // section, morphSection throws; because this runs in a detached
    // requestIdleCallback the rejection would otherwise surface as an unhandled
    // rejection (Sentry). Warn (breadcrumb-only, not an unhandled rejection) so
    // real failures stay observable in dev/preview, then leave the section
    // un-hydrated so a later navigation can retry.
    console.warn('[section-hydration] failed to hydrate', normalizedId, error);
  }
}

/**
 * Hydrates a section using the Section Rendering API preserving states, when
 * the DOM is ready.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {URL} [url] - The URL to render the section from
 */
export async function hydrate(sectionId, url) {
  onDocumentReady(() => {
    requestIdleCallback(() => hydrateSection(sectionId, url));
  });
}
